import {
  FLEET_VEHICLE_REQUEST_STATUSES,
  type FleetVehicleRequest,
  fleetVehicleRequestStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Select,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardPen } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MaskedNotice, PersonalValue } from "../../../features/customers/personal-data.js";
import { formatPlateDisplay } from "../../../features/customers/personal-data-values.js";
import {
  useFleetPermissions,
  useFleetProducts,
  useRefreshFleet,
  useVehicleRequests,
} from "../../../features/fleet/queries.js";
import { FleetPage, VehicleRequestStatusBadge } from "../../../features/fleet/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({
  status: z
    .union([fleetVehicleRequestStatusSchema, z.literal("ALL")])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/filo/arac-talepleri")({
  validateSearch: (search) => searchSchema.parse(search),
  component: VehicleRequestsRoute,
});

/**
 * Araç ekleme talepleri (ADR 0017 §17): filo portalından gelen talepler personel onayıyla ARACA dönüşür (araç customers
 * modülünün portundan açılır; plaka tekil). Varsayılan görünüm bekleyen kuyruktur. Kimse kendi talebini onaylamaz kuralı
 * burada doğal olarak sağlanır: talebi portal kullanıcısı açar, personel karar verir.
 */
function VehicleRequestsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <FleetPage title={t("menu.fleetVehicleRequests")} description={t("fleet.requests.description")}>
      <RequestsBody />
    </FleetPage>
  );
}

function RequestsBody(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useFleetPermissions();
  const status = search.status === "ALL" ? undefined : (search.status ?? "PENDING");
  const pages = useCursorPages(String(status));
  const list = useVehicleRequests({ status }, pages.cursor);
  const [deciding, setDeciding] = useState<{ request: FleetVehicleRequest; approve: boolean } | null>(null);
  const masked = (list.data?.items ?? []).some((row) => row.personalDataMasked);

  const columns = useMemo<DataTableColumn<FleetVehicleRequest>[]>(
    () => [
      {
        id: "createdAt",
        header: t("fleet.requests.createdAt"),
        hideable: false,
        exportValue: (row) => row.createdAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "counterparty",
        header: t("fleet.fields.counterparty"),
        exportValue: (row) => row.counterpartyName,
        cell: (row) => (
          <Link
            to="/filo/$counterpartyId"
            params={{ counterpartyId: row.counterpartyId }}
            className="underline underline-offset-4"
          >
            {row.counterpartyName}
          </Link>
        ),
      },
      {
        id: "plate",
        header: t("fleet.fields.plate"),
        exportValue: (row) => row.plate,
        cell: (row) => (
          <PersonalValue
            value={row.plate}
            masked={row.personalDataMasked}
            format={formatPlateDisplay}
            className="font-mono"
          />
        ),
      },
      {
        id: "model",
        header: t("fleet.fields.brandModel"),
        exportValue: (row) => [row.brand, row.model].filter(Boolean).join(" "),
        cell: (row) =>
          [row.brand, row.model].filter(Boolean).join(" ") || (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "note",
        header: t("fleet.requests.note"),
        exportValue: (row) => row.note,
        cell: (row) => row.note ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => row.status,
        cell: (row) => (
          <span className="grid gap-1">
            <VehicleRequestStatusBadge status={row.status} />
            {row.decisionNote ? (
              <span className="text-xs text-muted-foreground">{row.decisionNote}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        exportValue: () => null,
        cell: (row) =>
          row.status === "PENDING" && permissions.canManage ? (
            <span className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setDeciding({ request: row, approve: true })}>
                {t("fleet.requests.approve")}
                <span className="sr-only"> — {formatPlateDisplay(row.plate)}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeciding({ request: row, approve: false })}
              >
                {t("fleet.requests.reject")}
                <span className="sr-only"> — {formatPlateDisplay(row.plate)}</span>
              </Button>
            </span>
          ) : null,
      },
    ],
    [t, permissions.canManage],
  );

  return (
    <>
      <MaskedNotice masked={masked} />
      <DataTable
        caption={t("menu.fleetVehicleRequests")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ClipboardPen />}
            title={status === "PENDING" ? t("fleet.requests.emptyPending") : t("fleet.requests.emptyTitle")}
            description={t("fleet.requests.emptyDescription")}
          />
        }
        filters={
          <FormField label={t("common.status")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? "PENDING"}
                onChange={(event) =>
                  void navigate({
                    to: ".",
                    search: ((previous: Record<string, unknown>) =>
                      compactSearch({ ...previous, status: event.target.value })) as never,
                    replace: true,
                  })
                }
              >
                <option value="ALL">{t("common.all")}</option>
                {FLEET_VEHICLE_REQUEST_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(`fleet.requestStatus.${value}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <DecisionDialog target={deciding} onClose={() => setDeciding(null)} />
    </>
  );
}

function DecisionDialog({
  target,
  onClose,
}: {
  target: { request: FleetVehicleRequest; approve: boolean } | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFleet();
  const products = useFleetProducts({ enabled: target?.approve === true });
  const fuels = (products.data ?? []).filter(
    (product) => product.productType === "FUEL" || product.productType === "LPG",
  );
  const [fuelProductId, setFuelProductId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (target) {
      setFuelProductId(target.request.fuelProductId ?? "");
      setNote("");
      setError(null);
    }
  }, [target]);

  const decide = useMutation({
    mutationFn: async () => {
      const { request, approve } = target!;
      if (approve)
        return (
          await api.POST("/fleet/vehicle-requests/{id}/approve", {
            params: { path: { id: request.id } },
            body: {
              version: request.version,
              fuelProductId: fuelProductId || null,
              decisionNote: note.trim() || null,
              policy: null,
            },
          })
        ).data!;
      return (
        await api.POST("/fleet/vehicle-requests/{id}/reject", {
          params: { path: { id: request.id } },
          body: { version: request.version, decisionNote: note.trim() },
        })
      ).data!;
    },
    onSuccess: async () => {
      await refresh();
      toast({
        tone: "success",
        title: target?.approve ? t("fleet.requests.approved") : t("fleet.requests.rejected"),
      });
      onClose();
    },
    onError: (caught) =>
      setError(isApiError(caught, "VERSION_CONFLICT") ? t("fleet.conflict") : errorMessage(caught)),
  });

  const approve = target?.approve ?? true;
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next && !decide.isPending) onClose();
      }}
      title={approve ? t("fleet.requests.approveTitle") : t("fleet.requests.rejectTitle")}
      description={
        target
          ? `${target.request.counterpartyName} · ${formatPlateDisplay(target.request.plate)}`
          : undefined
      }
      dismissible={!decide.isPending}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          decide.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {approve ? (
          <>
            <p className="text-sm text-muted-foreground">{t("fleet.requests.approveHint")}</p>
            <FormField label={t("fleet.fields.fuelProduct")}>
              {(control) => (
                <Select
                  {...control}
                  value={fuelProductId}
                  onChange={(event) => setFuelProductId(event.target.value)}
                >
                  <option value="">{t("fleet.vehicles.noFuel")}</option>
                  {fuels.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        ) : null}
        <FormField
          label={t("fleet.requests.decisionNote")}
          required={!approve}
          {...(approve ? {} : { description: t("fleet.requests.rejectHint") })}
        >
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={decide.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={approve ? "primary" : "destructive"}
            loading={decide.isPending}
            disabled={!approve && note.trim().length < 3}
          >
            {approve ? t("fleet.requests.approve") : t("fleet.requests.reject")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
